/** The pocket's fact level (spec §7.2): the sentence, "subject → relation → object", "‹Status› · Applies in ‹X›", Sources › and
 * History ›, and Edit. A fact outside the loaded views (memory_graph open=<fact id>) is read on its own. */
import { useEffect, useState } from "react";
import { getFact, type MemoryFact } from "@/api/memory-focus";
import {
  Button,
  ConfirmDialog,
  Icons,
  PocketRow,
  PocketSection,
  PocketStatement,
  Spinner,
} from "@/components/ui";
import { useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { framesMemoryMessages } from "@/i18n/messages/frames-memory";
import { factPath, factStatusLine } from "@/lib/memory-thing-kinds";
import { useMemoryStore } from "@/stores/memory-store";
import { findFact } from "./memory-pocket-helpers";
import { PocketFactEditor } from "./pocket-fact-editor";
function useFact(id: string): { fact: MemoryFact | null; missing: boolean } {
  const known = useMemoryStore((s) => findFact(s, id));
  const [read, setRead] = useState<{
    id: string;
    fact: MemoryFact | null;
  } | null>(null);
  useEffect(() => {
    if (known) return;
    let active = true;
    getFact(id).then(
      (fact) => {
        if (active) setRead({ id, fact });
      },
      () => {
        if (active) setRead({ id, fact: null });
      },
    );
    return () => {
      active = false;
    };
  }, [id, known]);
  const fetched = read?.id === id ? read : null;
  return {
    fact: known ?? fetched?.fact ?? null,
    missing: !known && fetched !== null && fetched.fact === null,
  };
}
export function PocketFactLevel({ factId }: { factId: string }) {
  const t = useT(framesMemoryMessages);
  const common = useT(commonMessages);
  const { fact, missing } = useFact(factId);
  const [editing, setEditing] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState(false);
  if (missing)
    return (
      <PocketStatement
        size="empty"
        title={t("factMissing")}
        testId="memory-fact-missing"
      />
    );
  if (!fact)
    return (
      <div className="flex justify-center py-6">
        <Spinner className="h-4 w-4 text-muted" />
      </div>
    );
  const open = (level: "sources" | "history") =>
    useMemoryStore.getState().openLevel({ level, id: fact.id });
  const remove = async () => {
    if (removing) return;
    setRemoving(true);
    setRemoveError(false);
    try {
      await useMemoryStore.getState().removeFact(fact.id);
      setConfirmRemove(false);
    } catch {
      setConfirmRemove(false);
      setRemoveError(true);
    } finally {
      setRemoving(false);
    }
  };
  return (
    <div data-testid="memory-fact-level">
      <PocketStatement
        size="panel"
        title={fact.claim}
        testId="memory-fact-statement"
        line={
          <>
            <span className="block">{factPath(fact)}</span>
            <span className="block text-appkit-small font-medium">
              {factStatusLine(fact)}
            </span>
          </>
        }
      />
      {editing ? (
        <PocketFactEditor fact={fact} onDone={() => setEditing(false)} />
      ) : (
        <>
          <PocketSection>
            <PocketRow
              testId="memory-fact-sources"
              leading={
                <Icons.MessageCircle className="h-4 w-4" aria-hidden="true" />
              }
              title={t("sources")}
              chevron
              onSelect={() => open("sources")}
            />
            <PocketRow
              testId="memory-fact-history"
              leading={<Icons.Clock className="h-4 w-4" aria-hidden="true" />}
              title={t("history")}
              chevron
              onSelect={() => open("history")}
            />
          </PocketSection>
          {removeError ? (
            <p
              className="px-2 pt-2 text-appkit-caption text-danger"
              role="alert"
              data-testid="memory-fact-remove-error"
            >
              {t("removeFailed")}
            </p>
          ) : null}
          <div className="mt-3 flex gap-2 px-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setEditing(true)}
              data-testid="memory-fact-edit"
            >
              {common("edit")}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setConfirmRemove(true)}
              data-testid="memory-fact-remove"
            >
              {common("remove")}
            </Button>
          </div>
        </>
      )}
      {confirmRemove ? (
        <ConfirmDialog
          busy={removing}
          title={t("removeTitle")}
          description={t("removeDescription")}
          confirmLabel={removing ? t("removing") : common("remove")}
          onConfirm={() => void remove()}
          onCancel={() => {
            if (!removing) setConfirmRemove(false);
          }}
          data-testid="memory-fact-remove-confirm"
        />
      ) : null}
    </div>
  );
}
